/**
 * v4: plan → propose → evaluate → arrange. A small symbolic composer, not a
 * trained model. Research, assumptions and listening protocol:
 * docs/design/synth-music-v4-research.md.
 *
 * Identity streams do not depend on Adventure: changing it develops the SAME
 * theme. Four-bar phrases have explicit ancestry, cadences and novelty targets.
 * Reprises restore actual notes/harmony, not merely a section named "return".
 */
import {
  ARCHETYPES, KEYS, MODES, NOUNS, PALETTES, Rng, Scale,
  type InstId, type SNote, type SynthRequest, type SynthScore,
} from './compose';
import { makeV2Patches } from './instruments2';
import { GENRES, METERS, barBeats, groupStarts, meterRhythm, placeGenre, type GenreId, type MeterChoice, type MeterId } from './genres';

export const SYNTH_COMPOSER_VERSION_4 = 'synth-development-4';
export interface Synth4Options { adventure?: number; genre?: GenreId; meter?: MeterChoice }
export type Role = 'invitation' | 'theme' | 'answer' | 'development' | 'space' | 'return' | 'coda';
export type Gesture = 'original' | 'answer' | 'sequence' | 'inversion' | 'displacement' | 'expansion' | 'fragment' | 'recall';
export interface MotifNote { at: number; degree: number; length: number }
export interface MelodyNote { at: number; pitch: number; length: number; source: number }
export interface Chord { root: number; pcs: number[]; name: string; degree: number; borrowed: boolean }
export interface Plan { label: string; role: Role; bars: number; energy: number; novelty: number }
export interface Phrase4 {
  bar: number; bars: number; section: string; role: Role; gesture: Gesture;
  sourceBar: number | null; novelty: number; targetNovelty: number;
  cadence: 'open' | 'home'; chords: string[]; borrowed: boolean;
}
export interface SynthScore4 extends SynthScore {
  composer: 4;
  development: { adventure: number; motif: MotifNote[]; groove: string; phrases: Phrase4[] };
}
const mod = (n: number, d: number) => ((n % d) + d) % d;
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const pcDistance = (a: number, b: number) => Math.min(mod(a - b, 12), mod(b - a, 12));

export function planForm(req: SynthRequest, rng: Rng): Plan[] {
  const ambient = req.scene === 'exploration';
  const names = ambient ? ['First light', 'A signal', 'Reflection', 'Uncharted', 'Still water', 'Recognised', 'Horizon']
    : req.scene === 'fight' ? ['Standoff', 'Clash', 'Onslaught', 'Desperation', 'Breath', 'Last stand', 'Aftermath']
    : req.scene === 'flight' ? ['Ignition', 'Trajectory', 'Orbit', 'Slingshot', 'Weightless', 'Homeward', 'Approach']
    : ['Doorway', 'The thought', 'A reply', 'Elsewhere', 'Breathing room', 'Coming home', 'Afterglow'];
  const roles: Role[] = ['invitation', 'theme', 'answer', 'development', 'space', 'return', 'coda'];
  const lengths = [4, 8, 8, rng.chance(.5) ? 4 : 8, 4, 8, 4];
  const energy = ambient ? [.2, .38, .5, .6, .26, .66, .22] : req.scene === 'fight' ? [.55, .85, .92, 1, .5, 1, .4] : [.25, .6, .7, .82, .3, .9, .28];
  const novelty = [.18, .05, .2, .42, .24, .08, .16];
  return roles.map((role, i) => ({ role, label: names[i], bars: lengths[i], energy: energy[i] * (req.night ? (req.scene === 'fight' ? .93 : .83) : 1), novelty: novelty[i] }));
}

/** Generate a two-bar fingerprint from short rhythmic cells, with a breath. */
export function makeMotif(rng: Rng, meter: MeterId): MotifNote[] {
  const cells = meterRhythm(meter).cells, length = barBeats({ meter: METERS[meter] }) * 2;
  const starts = meter === '4/4' ? [0, 2, 4].flatMap(t => rng.pick(cells).map(x => t + x))
    : [0, length / 2].flatMap(t => rng.pick(cells).map(x => t + x)).filter(at => at < length - .6);
  if (meter === '4/4') starts.push(rng.pick([6, 6.5]));
  starts[0] = 0;
  let degree = rng.pick([0, 2, 4]), direction = rng.pick([-1, 1]);
  return starts.map((at, i) => {
    if (i > 0) {
      const leap = i === 2 ? rng.pick([2, 3, 4]) : rng.pick([0, 1, 1, 2]);
      if (i === 3 || degree > 6 || degree < 0) direction *= -1;
      degree = clamp(degree + leap * direction, -2, 9);
    }
    return { at, degree, length: Math.min(1.6, (starts[i + 1] ?? length - .6) - at - .14) };
  });
}

function diatonic(scale: Scale, degree: number): Chord {
  const pcs = [0, 2, 4, 6].map(d => mod(scale.pitch(degree + d), 12));
  const seventh = mod(pcs[3] - pcs[0], 12);
  const triad = mod(pcs[2] - pcs[0], 12) === 8 ? `${KEYS[pcs[0]]}+` : scale.name(degree);
  const name = triad + (seventh === 11 ? 'maj7' : '7');
  return { degree, root: pcs[0], pcs, name, borrowed: false };
}

/** Bounded beam search: cadence, common tones and a planned tension contour. */
export function planHarmony(scale: Scale, rng: Rng, role: Role, home: boolean, adventure: number, previousRoot: number): Chord[] {
  const palette = Array.from({ length: 7 }, (_, i) => diatonic(scale, i)).filter(c => scale.quality(c.degree) !== 'dim');
  const tension = [0, .7, .35, .4, .9, .3, .65];
  const target = role === 'development' ? [.4, .8, .6, home ? 0 : .85] : [.05, .4, .65, home ? 0 : .85];
  const openDegree = scale.steps[1] === 1 ? 1 : 4;
  let beam: { path: Chord[]; cost: number }[] = [{ path: [], cost: 0 }];
  for (let bar = 0; bar < 4; bar++) {
    const next: typeof beam = [];
    for (const state of beam) for (const chord of palette) {
      if (bar === 3 && chord.degree !== (home ? 0 : openDegree)) continue;
      if (bar === 0 && ['invitation', 'theme', 'return', 'coda'].includes(role) && chord.degree !== 0) continue;
      const last = state.path.at(-1);
      const shared = last ? last.pcs.filter(p => chord.pcs.includes(p)).length : 1;
      const repeated = last?.degree === chord.degree;
      const rootMotion = pcDistance(last?.root ?? previousRoot, chord.root);
      const cost = state.cost + Math.abs(tension[chord.degree] - target[bar]) * 2
        + (repeated ? 1.6 : 0) - shared * .12 + rootMotion * .035
        + rng.range(0, .65 + adventure * .7);
      next.push({ path: [...state.path, chord], cost });
    }
    beam = next.sort((a, b) => a.cost - b.cost).slice(0, 12);
  }
  const chords = beam[0].path;
  if (role === 'development' && adventure >= .35) {
    const minor = scale.steps[2] === 3;
    const root = mod(scale.key + (minor ? 5 : 8), 12);
    chords[1] = { root, pcs: [0, 4, 7, 11].map(d => mod(root + d, 12)),
      name: `${KEYS[root]}maj7*`, degree: 3, borrowed: true };
  }
  return chords;
}

/** Search inversions without clamping notes off their chord pitch classes. */
export function voiceLead(chord: Chord, previous: number[]): number[] {
  const candidates: number[][] = [];
  for (let inversion = 0; inversion < 4; inversion++) for (const octave of [48, 60]) {
    const v: number[] = [];
    for (let i = 0; i < 4; i++) {
      let pitch = octave + chord.pcs[(inversion + i) % 4];
      while (v.length && pitch <= v[v.length - 1]) pitch += 12;
      v.push(pitch);
    }
    for (const drop of [false, true]) {
      const open = [...v]; if (drop) open[2] -= 12;
      open.sort((a, b) => a - b);
      if (open[0] >= 48 && open[3] <= 81) candidates.push(open);
    }
  }
  const target = previous.length ? previous : [53, 60, 65, 71];
  const cost = (v: number[]) => v.reduce((sum, p, i) => sum + Math.abs(p - target[i]), 0)
    + (v[0] < 55 && v[1] - v[0] < 5 ? 4 : 0);
  return candidates.sort((a, b) => cost(a) - cost(b))[0];
}

export function nearest(pitch: number, pcs: number[], lo = 62, hi = 85): number {
  const choices = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i).filter(p => pcs.includes(mod(p, 12)));
  return choices.reduce((best, p) => Math.abs(p - pitch) < Math.abs(best - pitch) ? p : best, choices[0]);
}

/** Compare corresponding motif events, so a fragment isn't mistaken for noise.
 * Relative contour, timing and omitted events are heuristics, not human surprisal. */
function phraseDistance(a: MelodyNote[], b: MelodyNote[]): number {
  if (!a.length || !b.length) return 1;
  const matches = a.flatMap(n => { const other = b.find(m => m.source === n.source); return other ? [[n, other]] : []; });
  if (!matches.length) return 1;
  const missing = 1 - matches.length / Math.max(a.length, b.length);
  const distance = matches.reduce((sum, [n, m]) => sum
    + .65 * Math.min(1, Math.abs((n.pitch - a[0].pitch) - (m.pitch - b[0].pitch)) / 7)
    + .35 * Math.min(1, Math.abs(n.at - m.at)), 0) / matches.length;
  return Math.min(1, distance + missing * .25);
}

export function propose(motif: MotifNote[], gesture: Gesture, scale: Scale, chords: Chord[], rng: Rng, variant: number, meter: MeterId): MelodyNote[] {
  const bar = barBeats({ meter: METERS[meter] }), halfBeats = bar * 2, accents = groupStarts(meter);
  const raw: (MotifNote & { source: number })[] = [];
  for (let half = 0; half < 2; half++) for (let i = 0; i < motif.length; i++) {
    const n = motif[i];
    if (gesture === 'fragment' && i >= 3) continue;
    if (gesture === 'expansion' && i >= Math.ceil(motif.length / 2)) continue;
    let at = half * halfBeats + n.at * (gesture === 'expansion' ? 1.7 : 1);
    let degree = n.degree;
    if (gesture === 'inversion') degree = motif[0].degree * 2 - degree;
    if (gesture === 'sequence') degree += half ? -1 : 2;
    if (gesture === 'answer' && half) degree += i < 3 ? 1 : -1;
    if (gesture === 'displacement') at += .5;
    if (variant && i > 2 && rng.chance(.23)) degree += rng.pick([-1, 1]);
    if (half && i === motif.length - 1) degree = 0;
    if (at < (half + 1) * halfBeats - .6) raw.push({ at, degree, source: half * motif.length + i, length: n.length * (gesture === 'expansion' ? 1.5 : 1) });
  }
  // A sparse quotation still answers on the planned final chord.
  if (raw[raw.length - 1].at < bar * 3) raw.push({ at: bar * 3.5, degree: 0, source: motif.length * 2 - 1, length: Math.min(1.1, bar * .5 - .4) });
  let previous = 72 + scale.key;
  return raw.map((n, i) => {
    const chord = chords[Math.min(3, Math.floor(n.at / bar))];
    const strong = accents.some(at => Math.abs(mod(n.at, bar) - at) < .001), end = i === raw.length - 1;
    let pitch = scale.pitch(n.degree, 4);
    while (pitch < 64) pitch += 12;
    while (pitch > 85) pitch -= 12;
    // Weak beats may retain scale tones outside the chord.
    if (strong || end || chord.borrowed) pitch = nearest(end && i ? previous : pitch, end ? [chord.root] : chord.pcs);
    if (!end && i && Math.abs(pitch - previous) > 9) pitch = nearest(previous + Math.sign(pitch - previous) * 5, chord.pcs);
    previous = pitch;
    return { at: n.at, pitch, source: n.source, length: Math.max(.18, Math.min(n.length, (raw[i + 1]?.at ?? bar * 4 - .4) - n.at - .12)) };
  });
}

export function selectPhrase(motif: MotifNote[], theme: MelodyNote[], previous: MelodyNote[], role: Role, target: number,
  scale: Scale, chords: Chord[], rng: Rng, meter: MeterId): { melody: MelodyNote[]; gesture: Gesture; novelty: number } {
  const gestures: Gesture[] = role === 'space' || role === 'coda' || role === 'invitation' ? ['fragment', 'expansion']
    : role === 'development' ? ['sequence', 'inversion', 'displacement', 'expansion'] : ['answer', 'sequence', 'displacement'];
  let best: { melody: MelodyNote[]; gesture: Gesture; novelty: number; cost: number } | undefined;
  for (let i = 0; i < 20; i++) {
    const gesture = gestures[i % gestures.length], melody = propose(motif, gesture, scale, chords, rng, i, meter);
    const novelty = phraseDistance(melody, theme);
    const leaps = melody.slice(1).reduce((s, n, j) => s + Math.max(0, Math.abs(n.pitch - melody[j].pitch) - 5), 0) / melody.length;
    const join = previous.length ? Math.abs(melody[0].pitch - previous[previous.length - 1].pitch) * .025 : 0;
    const redundancy = previous.length && phraseDistance(melody, previous) < .035 ? .3 : 0;
    const cost = Math.abs(novelty - target) * 4 + leaps * .12 + join + redundancy + rng.range(0, .035);
    if (!best || cost < best.cost) best = { melody, gesture, novelty, cost };
  }
  return best!;
}

export function composeSynth4(req: SynthRequest, options: Synth4Options = {}): SynthScore4 {
  const adventure = Number.isFinite(options.adventure) ? clamp(options.adventure!, 0, 1) : .55;
  const chosen = options.genre === 'place' ? placeGenre(req) : options.genre;
  const genre = chosen && chosen !== 'scene' ? GENRES[chosen] : undefined;
  const meterId = options.meter && options.meter !== 'genre' ? options.meter : genre?.meter ?? '4/4';
  const meter = METERS[meterId], length = barBeats({ meter }), rhythm = meterRhythm(meterId), accents = groupStarts(meterId);
  const stream = (layer: string) => new Rng(req.seed, `v4|${req.biome}|${req.scene}|${req.night ? 'n' : 'd'}|${layer}`);
  const key = new Rng(req.seed, 'world').int(0, 11), modeRng = new Rng(req.seed, `mode2|${req.biome}`);
  modeRng.chance(.5); // Same base-mode draw as v2/v3.
  const mode = modeRng.pick(PALETTES[req.biome].modes), scale = new Scale(key, MODES[mode]);
  const palette = PALETTES[req.biome], archetype = ARCHETYPES[req.scene];
  const ambient = !genre && req.scene === 'exploration';
  const chill = genre ? ['waltz', 'jig'].includes(genre.feel) : req.scene === 'small-city';
  const flight = !genre && req.scene === 'flight';
  const rng = stream('form'), plan = planForm(req, rng);
  const tempo = genre?.tempo ?? archetype.tempo;
  const bpm = Math.round(rng.range(tempo[0], tempo[1]) * (req.night ? .9 : 1));
  const swing = meterId !== '4/4' ? 0 : chill ? .1 : req.scene === 'large-city' ? .025 : 0;
  const motif = makeMotif(new Rng(req.seed, `v4|${req.biome}|identity`), meterId);
  const patches = makeV2Patches(req);
  const grooveRng = stream('groove');
  const groove = ambient ? 'Free breathing' : meterId !== '4/4' ? METERS[meterId].label
    : genre?.feel === 'electronic' ? 'Motor pulse' : grooveRng.pick(chill ? ['Broken pocket', 'Late backbeat', 'Tresillo'] : ['Motor pulse', 'Broken pocket', 'Tresillo']);
  const kicks = meterId !== '4/4' ? rhythm.kicks : groove === 'Motor pulse' ? [0, 1, 2, 3] : groove === 'Tresillo' ? [0, 1.5, 3] : groove === 'Late backbeat' ? [0, 2.5] : [0, 1.75, 2.5];
  const bars = plan.reduce((sum, p) => sum + p.bars, 0), beats = bars * length;
  const notes: SNote[] = [], sections: SynthScore['sections'] = [], phrases: Phrase4[] = [], energy: number[] = [];
  const themeChords = planHarmony(scale, stream('theme-harmony'), 'theme', true, 0, key);
  const theme = propose(motif, 'original', scale, themeChords, stream('theme'), 0, meterId);
  let bar = 0, voicing: number[] = [], previous: MelodyNote[] = [], previousRoot = key;
  const human = stream('performance');
  const add = (inst: InstId, pitch: number, at: number, length: number, velocity: number, extra: Partial<SNote> = {}) => {
    // Timing offsets are shared by groove position, not independent jitter.
    const offbeat = Math.abs(mod(at, 1) - .5) < .001;
    const timed = at + (offbeat && inst !== 'pad' ? swing : 0);
    if (timed >= beats) return;
    notes.push({ inst, pitch, beat: timed, beats: Math.max(.04, Math.min(length, beats - timed)),
      vel: clamp(velocity, .1, 1), ...extra });
  };
  for (const section of plan) {
    const startBar = bar, startNote = notes.length;
    for (let local = 0; local < section.bars; local += 4) {
      const firstTheme = section.role === 'theme' && local === 0;
      const recall = section.role === 'return' && local === 0;
      const home = section.role === 'coda' || section.role === 'return' || local + 4 === section.bars;
      const restore = firstTheme || recall;
      const phraseRng = stream(`phrase:${bar}`);
      const chords = restore ? themeChords : planHarmony(scale, phraseRng, section.role, home, adventure, previousRoot);
      const target = clamp(section.novelty * (.4 + adventure * 1.1), 0, 1);
      const selected = restore ? { melody: theme.map(n => ({ ...n })), gesture: (recall ? 'recall' : 'original') as Gesture, novelty: 0 }
        : selectPhrase(motif, theme, previous, section.role, target, scale, chords, phraseRng, meterId);
      const melody = selected.melody;
      phrases.push({ bar, bars: 4, section: section.label, role: section.role, gesture: selected.gesture,
        sourceBar: firstTheme ? null : 4, novelty: selected.novelty, targetNovelty: target,
        cadence: home || restore ? 'home' : 'open', chords: chords.map(c => c.name), borrowed: chords.some(c => c.borrowed) });
      const melodyInst: InstId = section.role === 'space' || section.role === 'invitation' || section.role === 'coda' ? (ambient ? 'bell' : 'keys') : 'lead';
      for (const n of melody) add(melodyInst, n.pitch, bar * length + n.at, n.length,
        .63 + section.energy * .18 + human.range(-.045, .045), { bright: .75 + section.energy * .45, throw: n === melody[melody.length - 1] && !ambient });
      const occupied = (at: number, length = .5) => melody.some(n => at < n.at + n.length + .12 && at + length > n.at - .12);
      for (let b = 0; b < 4; b++) {
        const absoluteBar = bar + b, t = absoluteBar * length, chord = chords[b];
        const e = section.energy * (b === 3 ? .9 : 1);
        energy.push(e);
        voicing = voiceLead(chord, voicing);
        for (const pitch of voicing) add('pad', pitch, t, length, .37 + e * .14, { bright: .65 + e * .5 });
        const root = 36 + mod(chord.root, 12);
        const sparse = ambient || ['invitation', 'space', 'coda'].includes(section.role);
        if (sparse) add('bass', root, t, length - .15, .55);
        else {
          const bassRhythm = [...new Set([0, ...kicks.slice(1), ...(e > .7 && meterId !== '3/4' ? [length - .5] : [])])].sort((a, b) => a - b);
          bassRhythm.forEach((at, i) => {
            const nextRoot = chords[(b + 1) % 4].root;
            const approach = i === bassRhythm.length - 1 && at >= length - .5 && b < 3;
            const pitch = approach ? nearest(root, [mod(nextRoot - 1, 12), mod(nextRoot + 1, 12)], 33, 50)
              : root + (i === 1 && groove === 'Motor pulse' ? 12 : 0);
            add('bass', pitch, t + at, Math.min(.85, (bassRhythm[i + 1] ?? length) - at - .12), .74 + (i === 0 ? .13 : 0));
          });
        }
        // Accompaniment listens: no keys/arp attack beneath an active melody.
        if (melodyInst === 'lead') for (const at of rhythm.replies) {
          // Waltz accompaniment owns beats two and three below the melody.
          if (meterId !== '3/4' && occupied(b * length + at, .4)) continue;
          const inst = ambient ? 'bell' : 'keys';
          const tones = ambient ? [voicing[2] + 12] : [voicing[1], voicing[2], voicing[3]];
          for (const pitch of tones) add(inst, pitch, t + at, Math.min(.4, length - at - .12), meterId === '3/4' ? .34 : .44, { pan: -.22 });
        }
        // A reply quotes the tail of the theme, only in the melody's breath.
        if (!sparse && (b === 1 || b === 3)) for (const at of [length - 1, length - .5]) {
          if (occupied(b * length + at, .25)) continue;
          const pitch = nearest(scale.pitch(motif[motif.length - (at === length - 1 ? 2 : 1)].degree, 4), chord.pcs, 60, 81);
          add('bell', pitch, t + at, .3, .47, { pan: .28 });
        }
        if (ambient || sparse) continue;
        for (const at of kicks) add('kick', 36, t + at, .2, at === 0 ? .88 : .66);
        const snares = meterId === '4/4' && (req.night || groove === 'Late backbeat') ? [2] : rhythm.snares;
        for (const at of snares) add('snare', 38, t + at + (chill && meterId === '4/4' ? .018 : 0), .18, meterId === '3/4' ? .3 : .66);
        const hats = Array.from({ length: length * 2 }, (_, i) => i * .5).filter(at => !req.night || meterId !== '4/4' || at % 1);
        for (const at of hats) add(chill ? 'shaker' : 'hat', 42, t + at, .08,
          (accents.includes(at) ? .43 : .27) + human.range(-.03, .03), { pan: -.12 });
        // One local disruption at the phrase end; the next downbeat stays clear.
        if (b === 3 && section.role === 'development' && adventure > .65) {
          for (const at of [length - 1.25, length - .75, length - .25]) add('tom', at < length - 1 ? 48 : 43, t + at, .18, .48);
        }
        if ((flight || genre?.feel === 'electronic' || (!genre && req.scene === 'large-city')) && section.role === 'return') for (const at of hats.filter(at => at % 1)) {
          if (!occupied(b * length + at, .2)) add('arp', voicing[(b + Math.floor(at)) % 4] + 12, t + at, .2, .43, { pan: .2 });
        }
      }
      previous = melody; previousRoot = chords[3].root; bar += 4;
    }
    sections.push({ label: section.label, bar: startBar, bars: section.bars, layers: [...new Set(notes.slice(startNote).map(n => n.inst))] });
  }
  const words = stream('title'), title = `${words.pick(palette.words)} ${words.pick(NOUNS[req.scene])}`;
  // Only used patches need calibration. A/B shares the existing synthesis path.
  const used = new Set(notes.map(n => n.inst));
  for (const id of Object.keys(patches) as InstId[]) if (!used.has(id)) delete patches[id];
  return {
    version: SYNTH_COMPOSER_VERSION_4, composer: 4, request: { ...req }, title,
    style: (genre?.label ?? archetype.style) + (req.night ? ' (night)' : ''),
    meter: { numerator: meter.numerator, denominator: meter.denominator, grouping: [...meter.grouping] }, genre: chosen ?? 'scene',
    description: `A theme finds its way home. ${groove.toLowerCase()}, answering phrases, a harmonic detour and a recognisable return. Adventure ${Math.round(adventure * 100)}% changes how far the development travels; the seed's theme stays intact.`,
    bpm, key, keyName: KEYS[key], mode: mode[0].toUpperCase() + mode.slice(1), swing, bars, beats, duration: beats * 60 / bpm,
    progression: [...new Set(phrases.filter(p => p.role === 'theme' || p.role === 'development').map(p => `${p.role === 'theme' ? 'theme' : 'detour'} ${p.chords.join('–')}`))],
    sections, patches, notes: notes.sort((a, b) => a.beat - b.beat || a.pitch - b.pitch), energy,
    automation: energy.map((e, b) => ({ beat: b * length, tone: (req.night ? 4200 : 7500) + e * 4500 })),
    mix: { pad: -5, lead: -1.5, bell: -5, keys: -3, arp: -6, bass: -1, hat: -4, shaker: -3 },
    fx: { reverbSeconds: Math.min(5, palette.reverb * (ambient ? 1.1 : .8)), reverbTone: req.night ? 3300 : 5200,
      delayBeats: .75, delayFeedback: .26, drive: palette.drive * .5, master: .45,
      tone: req.night ? 5600 : 12000, duckDepth: ambient ? 0 : chill ? .16 : .26, width: .8 },
    development: { adventure, motif, groove, phrases },
  };
}
